import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { setLastPublishTimeEvent } from '@nx-frontend/shared/store/change-events-store';
import {
  getMenuItemsByParentIdFx,
  getMenuTypeFromPositionType,
  updateMenuItemEvent,
} from '@nx-frontend/shared/store/feature-menu-tree';
import { $pageInfo, updatePagePermissionsEvent } from '@nx-frontend/shared/store/portal-page';
import { ManageMembersView, Permissions } from '@nx-frontend/shared/types';
import InviteList from './invite-list/invite-list';
import NodeAccessList from './node-access-list/node-access-list';
import OrgAccessList from './org-access-list/org-access-list';
import { $currentUserCanInvite, $menuItem, $viewMode, updateViewModeEvent } from './store';

export function ManageMembersContent() {
  const [
    viewMode,
    pageInfo,
    menuItem,
    currentUserCanInvite,
    updateViewMode,
    getMenuItemsByParentId,
    updateMenuItem,
    updatePagePermissions,
    setLastPublishTime,
  ] = useUnit([
    $viewMode,
    $pageInfo,
    $menuItem,
    $currentUserCanInvite,
    updateViewModeEvent,
    getMenuItemsByParentIdFx,
    updateMenuItemEvent,
    updatePagePermissionsEvent,
    setLastPublishTimeEvent,
  ]);

  const t = useTranslations('features');
  const showTabs = viewMode === ManageMembersView.INVITE || viewMode === ManageMembersView.ORG_ACCESS_LIST;
  const activeTabIndex = viewMode || 0;

  const [tabUnderlineWidth, setTabUnderlineWidth] = useState(0);
  const [tabUnderlineLeft, setTabUnderlineLeft] = useState(0);

  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const onUpdateMenuItemPermissions = useCallback(
    async (menuItemId: string, permissions: Permissions) => {
      const menuType =
        menuItem && menuItem.positionType ? getMenuTypeFromPositionType(menuItem.positionType) : undefined;
      if (menuItem && menuType) {
        const items = await getMenuItemsByParentId({ parentId: menuItemId, menuType });
        items.push(menuItem);
        items.forEach((item) => {
          updateMenuItem({ menuType, item: { ...item, permissions } });
          if (pageInfo && item.pageId === pageInfo.pageId) {
            updatePagePermissions({ id: item.pageId, permissions });
          }
        });
        const time = new Date().getTime();
        setLastPublishTime(time);
      }
    },
    [getMenuItemsByParentId, menuItem, pageInfo, setLastPublishTime, updateMenuItem, updatePagePermissions]
  );

  useEffect(() => {
    function setTabPosition() {
      const currentTab = tabsRef.current[activeTabIndex];
      setTabUnderlineLeft(currentTab?.offsetLeft ?? 0);
      setTabUnderlineWidth(currentTab?.clientWidth ?? 0);
    }
    setTabPosition();
  }, [activeTabIndex]);

  const updateViewModeByIndex = (index: number) => {
    if (index > 0 && index < Object.keys(tabsData).length) {
      const selectedMode = Object.values(tabsData)[index].type;
      updateViewMode(selectedMode);
    }
  };

  const tabsData: Record<
    ManageMembersView,
    { type: ManageMembersView; label?: React.ReactNode; content: React.ReactNode }
  > = {
    [ManageMembersView.NODE_ACCESS]: {
      type: ManageMembersView.NODE_ACCESS,
      content: <NodeAccessList onUpdateMenuItemPermissions={onUpdateMenuItemPermissions} />,
    },
    [ManageMembersView.INVITE]: {
      type: ManageMembersView.INVITE,
      label: (
        <div className="flex items-center justify-center">
          <Icon className="w-4 h-4 mr-2 mt-0.5" icon={IconsMap.MAIL} />
          <span className="min-w-16 whitespace-nowrap">{t('manage-members.by_email')}</span>
        </div>
      ),
      content: <InviteList onUpdateMenuItemPermissions={onUpdateMenuItemPermissions} />,
    },
    [ManageMembersView.ORG_ACCESS_LIST]: {
      type: ManageMembersView.ORG_ACCESS_LIST,
      label: (
        <div className="flex items-center justify-center">
          <Icon className="w-4 h-4 mr-2 mt-0.5" icon={IconsMap.BUILDING} />
          <span className="min-w-32 whitespace-nowrap">{t('manage-members.from_organization')}</span>
        </div>
      ),
      content: <OrgAccessList onUpdateMenuItemPermissions={onUpdateMenuItemPermissions} />,
    },
  };

  return (
    <>
      {showTabs && (
        <div>
          <div className="relative">
            <div className="flex space-x-3 border-b gap-3">
              {Object.values(tabsData).map((tab, idx) => {
                if (!tab.label) return null;
                if (tab.type === ManageMembersView.INVITE && !currentUserCanInvite) return null;
                return (
                  <button
                    key={idx}
                    ref={(el) => (tabsRef.current[idx] = el)}
                    className={`py-2 border-b transition-colors duration-300 text-left ${
                      idx === activeTabIndex
                        ? 'border-cyan-900 text-cyan-900 font-bold'
                        : 'border-transparent text-neutral-500'
                    }`}
                    onClick={() => updateViewModeByIndex(idx)}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <span
              className="absolute bottom-[1px] block h-[1px] bg-cyan-900 transition-all duration-300"
              style={{ left: tabUnderlineLeft, width: tabUnderlineWidth }}
            />
          </div>
        </div>
      )}
      {Object.values(tabsData)[activeTabIndex].content ?? 'Error'}
    </>
  );
}

export default ManageMembersContent;
